import SegmentedButtonTsoPq from '@components/Buttons/SegmentedButtonTsoPq';
import CheckboxTsoPq from '@components/Checkbox/CheckboxTsoPq';
import ChipSelectorTsoPq, { ChipTsoPqProp } from '@components/ChipSelector/ChipSelectorTsoPq';
import DatepickerTsoPq from '@components/Datepicker/DatepickerTsoPq';
import SelectTsoPq from '@components/Select/SelectTsoPq';
import TextFieldTsoPq from '@components/TextField/TextFieldTsoPq';
import TreeAutocompleteTsoPq from '@components/TreeAutocomplete/TreeAutocompleteTsoPq';
import { TreeListItemTsoPqProps } from '@custom-types/TreeTypes';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

export type typeProps =
  | 'text'
  | 'singleSelect'
  | 'multipleSelect'
  | 'datepicker'
  | 'yearpicker'
  | 'timepicker'
  | 'datetimepicker'
  | 'integer'
  | 'floatingNumber'
  | 'chipSelector'
  | 'segmentedButton'
  | 'treeAutocomplete'
  | 'checkbox';

export type FormFieldTsoProps = {
  label?: string;
  name: string;
  type: typeProps;
  errorMessage?: string;
  options?: { id: number; label: string; value?: number }[];
  segmentedOptions?: { segmentValue: string; segmentLabel: string }[];
  autocompleteOptions?: TreeListItemTsoPqProps[];
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  rows?: number;
  customWidth?: string;
  register: any;
  enableFutureDates?: boolean;
  multiline?: boolean;
  labelDetail?: string;
  watch?: any;
  setValue?: any;
  errors?: any;
  trigger?: any;
  limitTags?: number;
  chips?: ChipTsoPqProp[];
  handleChipChange?: (chipsState: { id: number; name: string; active: boolean }[]) => void;
  handleChange?: (value: any) => void;
  activeChips?: { id: number; name: string }[];
  reset?: boolean;
  disabled?: boolean;
  defaultValue?: any;
  multiple?: boolean;
  supportingText?: string;
  minDateTime?: Date | Dayjs;
  isChecked?: boolean;
};

const FormFieldTsoPq = ({
  label = '',
  name,
  type,
  errorMessage,
  options = [],
  autocompleteOptions = [],
  segmentedOptions = [],
  placeholder,
  required = false,
  maxLength = 255,
  rows = 1,
  multiline = false,
  customWidth = '19.25rem',
  register,
  labelDetail,
  enableFutureDates = false,
  watch,
  setValue,
  errors,
  trigger,
  limitTags,
  chips,
  handleChipChange,
  handleChange,
  activeChips,
  reset,
  disabled = false,
  defaultValue,
  multiple,
  supportingText,
  minDateTime,
  isChecked = false,
}: FormFieldTsoProps) => {
  const { t } = useTranslation();

  const shouldSetNull = !required && type !== 'text';

  const customSetValueAs = shouldSetNull ? (value: any) => (value === '' ? null : value) : undefined;

  const requiredMessageMap = new Map([
    ['singleSelect', `${t('general.error.valueSelect')}`],
    ['multipleSelect', `${t('general.error.valueSelect')}`],
  ]);

  const commonProps = {
    ...register(name, {
      required: required ? (requiredMessageMap.get(type) ?? `${t(label)} ${t('general.error.required')}`) : undefined,
      setValueAs: customSetValueAs,
    }),
    mandatory: required,
    label: `${t(label)} ${labelDetail ?? ''}`,
    placeholder: placeholder ? t(placeholder) : undefined,
    customwidth: customWidth,
    clearInput: false,
    handleChange: () => {},
    type,
    watch,
    name,
    setValue,
    trigger,
    chips,
    handleChipChange,
    activeChips,
    disabled,
  };

  if (type === 'text' || type === 'integer' || type === 'floatingNumber') {
    return (
      <TextFieldTsoPq
        {...commonProps}
        multiline={multiline}
        rows={rows}
        error={!!errors?.[name]}
        supportingText={errors[name]?.message ?? supportingText}
        disabled={disabled}
      />
    );
  }

  if (type === 'singleSelect' || type === 'multipleSelect') {
    return (
      <SelectTsoPq
        {...commonProps}
        options={options}
        limitTags={limitTags}
        error={!!errors[name]}
        supportingText={errors[name]?.message ?? supportingText}
        multipleValues={type === 'multipleSelect'}
        disabled={disabled}
        handleChange={handleChange}
      />
    );
  }

  if (type === 'treeAutocomplete') {
    return (
      <TreeAutocompleteTsoPq
        {...commonProps}
        options={autocompleteOptions}
        limitTags={limitTags}
        error={!!errors[name]}
        supportingText={errors[name]?.message ?? supportingText}
        disabled={disabled}
        defaultValue={defaultValue}
        placeholder={placeholder}
        multiple={multiple}
        handleChange={handleChange}
        syncSelectedIdsToForm={!handleChange}
      />
    );
  }

  if (type === 'datepicker' || type == 'yearpicker' || type == 'timepicker' || type == 'datetimepicker') {
    return (
      <DatepickerTsoPq
        {...commonProps}
        width={customWidth}
        enableFutureDates={enableFutureDates}
        rows={rows}
        error={!!errors[name]}
        supportingText={errors[name]?.message ?? supportingText}
        minDateTime={minDateTime}
        disabled={disabled}
      />
    );
  }

  if (type === 'chipSelector') {
    return (
      <ChipSelectorTsoPq
        {...commonProps}
        customWidth={customWidth}
        chips={chips}
        handleChange={handleChipChange}
        activeChips={activeChips}
        error={!!errors[name]}
        supportingText={errors[name]?.message ?? supportingText}
        disabled={disabled}
      />
    );
  }

  if (type === 'segmentedButton') {
    return (
      <SegmentedButtonTsoPq
        {...commonProps}
        customWidth={customWidth}
        options={segmentedOptions}
        error={!!errors[name]}
        supportingText={errors[name]?.message ?? supportingText}
        disabled={disabled}
      />
    );
  }

  if (type === 'checkbox') {
    return (
      <CheckboxTsoPq {...commonProps} type="primary" disabled={disabled} checked={isChecked} onChange={handleChange} />
    );
  }
};

export default FormFieldTsoPq;
